import type { GridV1EditorMode } from './grid-v1-toolbar';
import type { GridStackWidget } from 'gridstack';

import { Box, IconButton, Menu, Portal, Text } from '@chakra-ui/react';
import { GridStack } from 'gridstack';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FiEdit2, FiMoreVertical, FiTrash2 } from 'react-icons/fi';

import 'gridstack/dist/gridstack.min.css';
import './grid-v1-editor.scss';

import { MapBlock } from '#components/document-viewer/map/map-block';
import {
  mapConfigToEditValues,
  mapEditValuesToConfig,
  normalizeMapConfig,
  stringifyMapConfig,
  type MapEditRequest,
} from '#components/document-viewer/map/map-config';
import { MapEditModal } from '#components/document-viewer/map/map-edit-modal';
import { MessageBlockPanel } from '#components/document-viewer/message/message-block-panel';
import {
  messageConfigToEditValues,
  messageEditValuesToConfig,
  normalizeMessageConfig,
  stringifyMessageConfig,
  type MessageEditRequest,
} from '#components/document-viewer/message/message-config';
import { MessageEditModal } from '#components/document-viewer/message/message-edit-modal';
import { useDocumentContext } from '#pages/document/document-context';

import {
  createDefaultGridV1CellContent,
  defaultNewGridV1WidgetLayout,
  nextGridV1CellId,
  type GridV1CellContent,
} from './grid-v1-cells';
import {
  mergeGridV1LayoutWithContent,
  mergeGridV1StoredLayoutWithContent,
} from './grid-v1-serialize';
import {
  DEFAULT_GRID_V1_GRID_OPTIONS,
  type GridV1CellFormat,
  type GridV1DocumentValue,
  type GridV1GridOptions,
  type GridV1Widget,
  toLayoutWidgets,
} from './grid-v1-types';
import {
  MarkdownEditor,
  type GridCellMarkdownEditorProps,
} from './markdown-editor';

export interface GridV1EditorHandle {
  addCell: () => void;
}

interface GridV1EditorProps {
  mode: GridV1EditorMode;
  value: GridV1DocumentValue;
  onChange: (next: GridV1DocumentValue) => void;
  cellMarkdownEditorProps: GridCellMarkdownEditorProps;
}

interface ParsedGridJsonConfig<T> {
  data: T;
  parseError: string | null;
}

function layoutWidgetsKey(widgets: GridV1DocumentValue['widgets']): string {
  return JSON.stringify(toLayoutWidgets(widgets));
}

function collectPortalTargets(container: HTMLElement): Map<string, Element> {
  const next = new Map<string, Element>();
  container.querySelectorAll('.grid-stack-item').forEach((item) => {
    const id =
      item.getAttribute('gs-id') ??
      item.getAttribute('id') ??
      item.getAttribute('data-gs-id');
    const content = item.querySelector('.grid-stack-item-content');
    if (id && content) {
      next.set(id, content);
    }
  });
  return next;
}

function findGridStackItemById(
  container: HTMLElement,
  cellId: string,
): HTMLElement | null {
  const escapedId = CSS.escape(cellId);
  const item = container.querySelector(
    `.grid-stack-item[gs-id="${escapedId}"], .grid-stack-item[id="${escapedId}"], .grid-stack-item[data-gs-id="${escapedId}"]`,
  );
  return item instanceof HTMLElement ? item : null;
}

function parseGridJsonConfig<T>(
  value: string,
  normalize: (input: unknown) => T,
  fallbackMessage: string,
): ParsedGridJsonConfig<T> {
  const trimmed = value.trim();
  if (!trimmed) {
    return { data: normalize({}), parseError: null };
  }

  try {
    return {
      data: normalize(JSON.parse(trimmed) as unknown),
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalize({}),
      parseError: error instanceof Error ? error.message : fallbackMessage,
    };
  }
}

interface GridV1CellViewProps {
  widget: GridV1Widget;
  isViewMode: boolean;
  markdownEditorProps: GridCellMarkdownEditorProps;
  documentId?: string;
  isNewDocument: boolean;
  getSerializedDocument: () => string;
  onValueChange: (cellId: string, value: string) => void;
  onFormatChange: (cellId: string, format: GridV1CellFormat) => void;
  onDelete: (cellId: string) => void;
  onEdit: (cellId: string) => void;
}

const GridV1CellView = memo(function GridV1CellView({
  widget,
  isViewMode,
  markdownEditorProps,
  documentId,
  isNewDocument,
  getSerializedDocument,
  onValueChange,
  onFormatChange,
  onDelete,
  onEdit,
}: GridV1CellViewProps) {
  const canEditCellModal =
    widget.format === 'np-map' || widget.format === 'np-message';

  const stopPointerPropagation = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  const actions = isViewMode ? (
    canEditCellModal ? (
      <Box
        className="grid-v1-cell-actions"
        onPointerDown={stopPointerPropagation}
      >
        <IconButton
          aria-label="Edit cell"
          size="xs"
          variant="outline"
          className="grid-v1-cell-actions__trigger"
          onClick={() => onEdit(widget.id)}
        >
          <FiEdit2 />
        </IconButton>
      </Box>
    ) : null
  ) : (
    <Box
      className="grid-v1-cell-actions"
      onPointerDown={stopPointerPropagation}
    >
      <Menu.Root positioning={{ placement: 'bottom-end' }}>
        <Menu.Trigger asChild>
          <IconButton
            aria-label="Cell actions"
            size="xs"
            variant="outline"
            className="grid-v1-cell-actions__trigger"
          >
            <FiMoreVertical />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content>
              {canEditCellModal ? (
                <Menu.Item value="edit" onClick={() => onEdit(widget.id)}>
                  <FiEdit2 />
                  Edit
                </Menu.Item>
              ) : null}
              <Menu.Item
                value="change-markdown"
                disabled={widget.format === 'markdown-v1'}
                onClick={() => onFormatChange(widget.id, 'markdown-v1')}
              >
                Change to Markdown
              </Menu.Item>
              <Menu.Item
                value="change-map"
                disabled={widget.format === 'np-map'}
                onClick={() => onFormatChange(widget.id, 'np-map')}
              >
                Change to Map
              </Menu.Item>
              <Menu.Item
                value="change-message"
                disabled={widget.format === 'np-message'}
                onClick={() => onFormatChange(widget.id, 'np-message')}
              >
                Change to Messages
              </Menu.Item>
              <Menu.Item
                value="delete"
                color="red.600"
                onClick={() => onDelete(widget.id)}
              >
                <FiTrash2 />
                Delete
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Box>
  );

  if (widget.format === 'np-map') {
    const parsed = parseGridJsonConfig(
      widget.value,
      normalizeMapConfig,
      'Could not parse map JSON',
    );
    return (
      <Box className="grid-v1-cell-content grid-v1-cell-content--custom grid-v1-cell-content--map">
        {actions}
        <MapBlock
          config={parsed.data}
          parseError={parsed.parseError}
          variant="grid-cell"
        />
      </Box>
    );
  }

  if (widget.format === 'np-message') {
    const parsed = parseGridJsonConfig(
      widget.value,
      normalizeMessageConfig,
      'Could not parse message JSON',
    );
    return (
      <Box className="grid-v1-cell-content grid-v1-cell-content--custom">
        {actions}
        {parsed.parseError ? (
          <Text fontSize="sm" color="orange.600">
            Invalid message config: {parsed.parseError}
          </Text>
        ) : null}
        <MessageBlockPanel
          config={parsed.data}
          documentId={documentId}
          isNewDocument={isNewDocument}
          getSerializedDocument={getSerializedDocument}
        />
      </Box>
    );
  }

  return (
    <Box className="grid-v1-cell-content grid-v1-cell-content--markdown">
      {actions}
      <MarkdownEditor
        {...markdownEditorProps}
        variant="grid-cell"
        value={widget.value}
        onChange={(next) => onValueChange(widget.id, next)}
      />
    </Box>
  );
});

export const GridV1Editor = forwardRef<GridV1EditorHandle, GridV1EditorProps>(
  function GridV1Editor(
    { mode, value, onChange, cellMarkdownEditorProps },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<GridStack | null>(null);
    const cellContentByIdRef = useRef<Map<string, GridV1CellContent>>(
      new Map(),
    );
    const onChangeRef = useRef(onChange);
    const gridOptionsRef = useRef<GridV1GridOptions>(value.grid);
    const valueRef = useRef(value);
    const modeRef = useRef(mode);
    const layoutKeyRef = useRef('');
    const [portalTargets, setPortalTargets] = useState<Map<string, Element>>(
      () => new Map(),
    );
    const [mapEditRequest, setMapEditRequest] = useState<MapEditRequest | null>(
      null,
    );
    const [messageEditRequest, setMessageEditRequest] =
      useState<MessageEditRequest | null>(null);
    const {
      documentId,
      isNewDocument,
      getSerializedDocument,
      registerGridContentFlush,
    } = useDocumentContext();

    const isViewMode = mode === 'view';

    onChangeRef.current = onChange;
    gridOptionsRef.current = value.grid;
    valueRef.current = value;
    modeRef.current = mode;

    const syncContentMapFromValue = useCallback((doc: GridV1DocumentValue) => {
      const map = new Map<string, GridV1CellContent>();
      for (const widget of doc.widgets) {
        map.set(widget.id, {
          format: widget.format,
          value: widget.value,
        });
      }
      cellContentByIdRef.current = map;
    }, []);

    const contentEmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    const emitMergedDocument = useCallback(() => {
      const grid = gridRef.current;
      if (!grid) return;

      const layout = grid.save(false) as Array<Record<string, unknown>>;
      const merged = mergeGridV1LayoutWithContent(
        layout,
        cellContentByIdRef.current,
        gridOptionsRef.current,
      );
      onChangeRef.current(merged);
    }, []);

    const emitCellContentChange = useCallback(() => {
      const merged = mergeGridV1StoredLayoutWithContent(
        valueRef.current,
        cellContentByIdRef.current,
      );
      onChangeRef.current(merged);
    }, []);

    const scheduleCellContentEmit = useCallback(() => {
      if (contentEmitTimeoutRef.current) {
        clearTimeout(contentEmitTimeoutRef.current);
      }

      contentEmitTimeoutRef.current = setTimeout(() => {
        contentEmitTimeoutRef.current = null;
        emitCellContentChange();
      }, 250);
    }, [emitCellContentChange]);

    const flushCellContentEmit = useCallback(() => {
      if (contentEmitTimeoutRef.current) {
        clearTimeout(contentEmitTimeoutRef.current);
        contentEmitTimeoutRef.current = null;
      }

      emitCellContentChange();
    }, [emitCellContentChange]);

    const refreshPortalTargets = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      setPortalTargets(collectPortalTargets(container));
    }, []);

    const applyGridStaticMode = useCallback(
      (grid: GridStack, nextMode: GridV1EditorMode) => {
        grid.setStatic(nextMode === 'view');
      },
      [],
    );

    const addCell = useCallback(() => {
      const grid = gridRef.current;
      if (!grid || modeRef.current !== 'edit') return;

      const doc = valueRef.current;
      const id = nextGridV1CellId(doc.widgets);
      const columnCount =
        doc.grid.column ?? DEFAULT_GRID_V1_GRID_OPTIONS.column ?? 12;
      const { w, h } = defaultNewGridV1WidgetLayout(columnCount);
      grid.addWidget({ id, w, h });
    }, []);

    useImperativeHandle(ref, () => ({ addCell }), [addCell]);

    useEffect(() => {
      syncContentMapFromValue(value);
    }, [syncContentMapFromValue, value]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const opts = {
        ...DEFAULT_GRID_V1_GRID_OPTIONS,
        ...value.grid,
        float: true,
      };

      const grid = GridStack.init(opts, container);
      gridRef.current = grid;
      applyGridStaticMode(grid, modeRef.current);

      const onLayoutChange = () => {
        emitMergedDocument();
      };

      grid.on('change', onLayoutChange);
      grid.on('resizestop', onLayoutChange);
      grid.on('dragstop', onLayoutChange);

      grid.on('removed', (_event, items) => {
        for (const item of items) {
          const id = item.id;
          if (id) {
            cellContentByIdRef.current.delete(id);
          }
        }
        refreshPortalTargets();
        emitMergedDocument();
      });

      grid.on('added', (_event, items) => {
        for (const item of items) {
          const id = item.id;
          if (id && !cellContentByIdRef.current.has(id)) {
            cellContentByIdRef.current.set(
              id,
              createDefaultGridV1CellContent(),
            );
          }
        }
        refreshPortalTargets();
        emitMergedDocument();
      });

      const layoutKey = layoutWidgetsKey(value.widgets);
      layoutKeyRef.current = layoutKey;
      grid.load(toLayoutWidgets(value.widgets) as GridStackWidget[]);
      requestAnimationFrame(() => {
        refreshPortalTargets();
      });

      return () => {
        grid.off('change');
        grid.off('resizestop');
        grid.off('dragstop');
        grid.off('removed');
        grid.off('added');
        grid.destroy(false);
        gridRef.current = null;
      };
      // Init once; external layout reload handled below.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only GridStack init
    }, []);

    useEffect(() => {
      const grid = gridRef.current;
      if (!grid) return;

      applyGridStaticMode(grid, mode);
    }, [applyGridStaticMode, mode]);

    useEffect(() => {
      const grid = gridRef.current;
      if (!grid) return;

      const layoutKey = layoutWidgetsKey(value.widgets);
      if (layoutKey === layoutKeyRef.current) return;

      layoutKeyRef.current = layoutKey;
      syncContentMapFromValue(value);
      grid.load(toLayoutWidgets(value.widgets) as GridStackWidget[]);
      requestAnimationFrame(() => {
        refreshPortalTargets();
      });
    }, [refreshPortalTargets, syncContentMapFromValue, value]);

    const handleCellValueChange = useCallback(
      (cellId: string, nextValue: string) => {
        const current =
          cellContentByIdRef.current.get(cellId) ??
          createDefaultGridV1CellContent();
        cellContentByIdRef.current.set(cellId, {
          ...current,
          value: nextValue,
        });
        scheduleCellContentEmit();
      },
      [scheduleCellContentEmit],
    );

    useEffect(
      () => () => {
        if (contentEmitTimeoutRef.current) {
          clearTimeout(contentEmitTimeoutRef.current);
        }
      },
      [],
    );

    useEffect(() => {
      registerGridContentFlush(flushCellContentEmit);
      return () => registerGridContentFlush(null);
    }, [flushCellContentEmit, registerGridContentFlush]);

    const handleCellFormatChange = useCallback(
      (cellId: string, format: GridV1CellFormat) => {
        if (modeRef.current === 'view') return;

        cellContentByIdRef.current.set(
          cellId,
          createDefaultGridV1CellContent(format),
        );
        emitMergedDocument();
      },
      [emitMergedDocument],
    );

    const handleCellDelete = useCallback((cellId: string) => {
      if (modeRef.current === 'view') return;

      const grid = gridRef.current;
      const container = containerRef.current;
      const item = container ? findGridStackItemById(container, cellId) : null;
      if (grid && item) {
        grid.removeWidget(item);
        return;
      }

      cellContentByIdRef.current.delete(cellId);
      onChangeRef.current({
        ...valueRef.current,
        widgets: valueRef.current.widgets.filter((w) => w.id !== cellId),
      });
    }, []);

    const handleCellEdit = useCallback(
      (cellId: string) => {
        const content =
          cellContentByIdRef.current.get(cellId) ??
          valueRef.current.widgets.find((w) => w.id === cellId) ??
          createDefaultGridV1CellContent();

        if (content.format === 'np-map') {
          const parsed = parseGridJsonConfig(
            content.value,
            normalizeMapConfig,
            'Could not parse map JSON',
          );
          setMapEditRequest({
            mode: 'edit',
            initialValues: mapConfigToEditValues(parsed.data),
            onCommit: async (next) => {
              const normalized = mapEditValuesToConfig(next);
              cellContentByIdRef.current.set(cellId, {
                format: 'np-map',
                value: stringifyMapConfig(normalized),
              });
              emitMergedDocument();
            },
          });
          return;
        }

        if (content.format === 'np-message') {
          const parsed = parseGridJsonConfig(
            content.value,
            normalizeMessageConfig,
            'Could not parse message JSON',
          );
          setMessageEditRequest({
            mode: 'edit',
            initialValues: messageConfigToEditValues(parsed.data),
            onCommit: async (next) => {
              const normalized = messageEditValuesToConfig(next);
              cellContentByIdRef.current.set(cellId, {
                format: 'np-message',
                value: stringifyMessageConfig(normalized),
              });
              emitMergedDocument();
            },
          });
        }
      },
      [emitMergedDocument],
    );

    const widgetForCell = useCallback(
      (cellId: string): GridV1Widget =>
        value.widgets.find((w) => w.id === cellId) ?? {
          id: cellId,
          x: 0,
          y: 0,
          w: 4,
          h: 2,
          ...createDefaultGridV1CellContent(),
        },
      [value.widgets],
    );

    return (
      <Box
        className={
          isViewMode ? 'grid-v1-editor grid-v1-editor--view' : 'grid-v1-editor'
        }
        flex={1}
        minH={0}
        display="flex"
        flexDirection="column"
      >
        <Box
          ref={containerRef}
          className="grid-stack grid-v1-editor-stack"
          flex={1}
        />
        <MapEditModal
          request={mapEditRequest}
          onDismiss={() => setMapEditRequest(null)}
        />
        <MessageEditModal
          request={messageEditRequest}
          onDismiss={() => setMessageEditRequest(null)}
        />
        {Array.from(portalTargets.entries()).map(([cellId, target]) =>
          createPortal(
            <GridV1CellView
              key={cellId}
              widget={widgetForCell(cellId)}
              isViewMode={isViewMode}
              markdownEditorProps={cellMarkdownEditorProps}
              documentId={documentId}
              isNewDocument={isNewDocument}
              getSerializedDocument={getSerializedDocument}
              onValueChange={handleCellValueChange}
              onFormatChange={handleCellFormatChange}
              onDelete={handleCellDelete}
              onEdit={handleCellEdit}
            />,
            target,
            cellId,
          ),
        )}
      </Box>
    );
  },
);
